import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';

// Paste a conversation, a few messages or one remark; the model reads it in
// the background. The panel lists every recent import with its state and, when
// expanded, the cards it produced as a table — the same table for three cards
// and for thirty. Cards the parser filed under another quest (or under no quest
// while the scope was unclear) wait here: one bar files them all under the
// quest the parser named, a toggle opens per-card choices.

const STATUS_LABEL = { pending: 'Queued', parsing: 'Parsing…', done: 'Done', failed: 'Failed' };

const targetsLabel = (t) => (t.targets || []).map((g) => `${g.pointNo != null ? `p${g.pointNo}` : '—'}${g.field ? ` ${g.field}` : ''}`).join(', ');

export default function ImportPanel({ questId, imports, onImported, onSelectTask, quests }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [open, setOpen] = useState(null);   // expanded import id
  const [detail, setDetail] = useState({}); // id -> full import row
  const [assign, setAssign] = useState({}); // key -> questId | 'skip'
  const [bulkQuest, setBulkQuest] = useState({}); // import id -> questId
  const [perCard, setPerCard] = useState({});     // import id -> bool
  const [showNotes, setShowNotes] = useState({}); // import id -> bool

  const questName = useMemo(() => {
    const m = new Map((quests || []).map((q) => [q.quest_id, q.quest_name || '']));
    return (id) => (m.has(id) && m.get(id) ? `${m.get(id)} (${id})` : id);
  }, [quests]);

  const submit = async (force = false) => {
    const t = text.trim();
    if (!t) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.importText(questId, t, force);
      if (r.duplicate) setNotice('This exact text was imported before — its cards already exist. Use “Import anyway” to run the parse again.');
      else { setText(''); setNotice('Parsing in the background — cards appear as soon as the model is done.'); setOpen(r.import && r.import.id); }
      if (onImported) onImported(r.import);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const detailSeq = useRef({});
  const loadDetail = async (id) => {
    const seq = (detailSeq.current[id] = (detailSeq.current[id] || 0) + 1);
    try {
      const r = await api.importRead(id);
      if (seq !== detailSeq.current[id]) return;
      setDetail((d) => ({ ...d, [id]: r.import }));
    } catch (e) { setError(e.message); }
  };
  useEffect(() => { if (open) loadDetail(open); }, [open, imports]); // eslint-disable-line react-hooks/exhaustive-deps

  const retry = async (id) => { try { await api.importRetry(id); } catch (e) { setError(e.message); } };
  const applyAssignments = async (id, a) => {
    if (!Object.keys(a).length) { setError('Pick a quest (or Skip) for at least one card first'); return; }
    setBusy(true); setError(null);
    try { await api.importApply(id, a); await loadDetail(id); if (onImported) onImported(null); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const applyBulk = (id, keys, target) => applyAssignments(id, Object.fromEntries(keys.map((k) => [k, target])));
  const applyPerCard = (id, keys) => applyAssignments(id, Object.fromEntries(keys.filter((k) => assign[k]).map((k) => [k, assign[k]])));

  return (
    <div className="tk-import">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} aria-label="Remarks to import"
        placeholder={'Paste a Telegram conversation, a few messages, one remark or a summary of what was agreed.\nExample: “7/19 wrong stress, should be рЕгент (2 places). 8/19 in the after-message the same.”'} />
      <div className="tk-import-actions">
        <button type="button" className="tk-btn tk-btn-primary" onClick={() => submit(false)} disabled={busy || !text.trim()}>{busy ? 'Sending…' : 'Import remarks'}</button>
        <button type="button" className="tk-btn tk-btn-ghost" onClick={() => submit(true)} disabled={busy || !text.trim()} title="Parse again even if this text was imported before">Import anyway</button>
        {notice ? <span className="tk-notice">{notice}</span> : null}
        {error ? <span className="tk-error">{error}</span> : null}
      </div>
      {imports && imports.length ? (
        <div className="tk-import-list">
          {imports.map((im) => {
            const full = detail[im.id] || im;
            const result = full.result || null;
            const filed = (result && result.filed) || null;
            const pending = (filed && filed.pending) || [];
            const tasks = (result && result.tasks) || [];
            const pendingTasks = tasks.filter((t) => pending.includes(t.key));
            const scope = (result && result.quest_scope) || {};
            const suggested = (scope.quest_ids || []).find((q) => q !== questId) || (scope.quest_ids || [])[0] || '';
            const bulk = bulkQuest[im.id] !== undefined ? bulkQuest[im.id] : suggested;
            const isOpen = open === im.id;
            const toggle = () => setOpen(isOpen ? null : im.id);
            const others = ((result && result.messages) || []).filter((m) => m.kind !== 'remark' && m.kind !== 'header');
            return (
              <div key={im.id} className={`tk-import-row st-${im.status}`}>
                <div className="tk-import-head" onClick={toggle} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') toggle(); }} aria-expanded={isOpen}>
                  <span className="tk-caret">{isOpen ? '▾' : '▸'}</span>
                  <span className={`tk-chip st-${im.status}`}>{STATUS_LABEL[im.status] || im.status}</span>
                  {filed ? (
                    <span className="tk-import-counts">
                      <strong>{tasks.length} card{tasks.length === 1 ? '' : 's'}</strong>
                      {filed.created.length ? <span> · {filed.created.length} created</span> : null}
                      {filed.appended.length ? <span> · {filed.appended.length} merged</span> : null}
                      {pending.length ? <span className="tk-warn"> · {pending.length} waiting for a quest</span> : null}
                    </span>
                  ) : null}
                  <span className="tk-import-text">{im.raw_head}{im.raw_chars > 300 ? '…' : ''}</span>
                  <span className="tk-muted tk-import-when">{im.created_by} · {new Date(im.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  {im.status === 'failed' ? <button type="button" className="tk-btn tk-btn-small" onClick={(e) => { e.stopPropagation(); retry(im.id); }}>Retry</button> : null}
                </div>
                {isOpen ? (
                  <div className="tk-import-detail">
                    {im.status === 'failed' ? (
                      <div className="tk-error-block">
                        <div><strong>Parse failed:</strong> {full.error}</div>
                        <div className="tk-muted">The pasted text is kept; nothing was invented. Retry when the model is reachable.</div>
                        {full.error_detail ? <details className="tk-fold"><summary>Diagnostics</summary><pre className="tk-pre">{full.error_detail}</pre></details> : null}
                      </div>
                    ) : null}
                    {im.status === 'parsing' || im.status === 'pending' ? <div className="tk-muted">The model is reading the text against the quest. This takes a minute or two.</div> : null}
                    {result ? (
                      <>
                        {scope.note ? <div className="tk-scope"><span className="tk-scope-label">How the text was read</span>{scope.note}</div> : null}

                        {pendingTasks.length ? (
                          <div className="tk-bulk">
                            <div className="tk-bulk-text">
                              <strong>{pendingTasks.length} card{pendingTasks.length === 1 ? '' : 's'}</strong> wait for a quest
                              {suggested ? <span> — the parser read them as <strong>{questName(suggested)}</strong>, not the open quest.</span> : <span> — the parser could not tell which quest they belong to.</span>}
                            </div>
                            <div className="tk-bulk-actions">
                              <select value={bulk} onChange={(e) => setBulkQuest((s) => ({ ...s, [im.id]: e.target.value }))} aria-label="Quest for all waiting cards">
                                <option value="">— pick a quest —</option>
                                {(quests || []).map((q) => <option key={q.quest_id} value={q.quest_id}>{q.quest_name || q.quest_id} · {q.quest_id}</option>)}
                              </select>
                              <button type="button" className="tk-btn tk-btn-primary tk-btn-small" disabled={busy || !bulk} onClick={() => applyBulk(im.id, pendingTasks.map((t) => t.key), bulk)}>File all {pendingTasks.length} there</button>
                              <button type="button" className="tk-btn tk-btn-small" disabled={busy} onClick={() => applyBulk(im.id, pendingTasks.map((t) => t.key), 'skip')}>Discard all</button>
                              <button type="button" className="tk-linkbtn" onClick={() => setPerCard((s) => ({ ...s, [im.id]: !s[im.id] }))}>{perCard[im.id] ? 'hide per-card choice' : 'choose per card'}</button>
                              {perCard[im.id] ? <button type="button" className="tk-btn tk-btn-small" disabled={busy} onClick={() => applyPerCard(im.id, pendingTasks.map((t) => t.key))}>File the chosen ones</button> : null}
                            </div>
                          </div>
                        ) : null}

                        {tasks.length ? (
                          <>
                            <div className="tk-tbl-tools">
                              <span className="tk-muted">{tasks.length} card{tasks.length === 1 ? '' : 's'} read from {result.messages ? result.messages.length : '?'} message{result.messages && result.messages.length === 1 ? '' : 's'}</span>
                              {tasks.some((t) => t.uncertainty) ? <button type="button" className="tk-linkbtn" onClick={() => setShowNotes((s) => ({ ...s, [im.id]: !s[im.id] }))}>{showNotes[im.id] ? 'hide the parser’s notes' : `show the parser’s notes (${tasks.filter((t) => t.uncertainty).length})`}</button> : null}
                            </div>
                            <table className="tk-tbl">
                              <thead>
                                <tr><th>#</th><th>Kind</th><th>Card</th><th>Where</th><th>Quest</th><th>State</th>{perCard[im.id] ? <th>File under</th> : null}</tr>
                              </thead>
                              <tbody>
                                {tasks.map((t, i) => {
                                  const c = filed && filed.created.find((x) => x.key === t.key);
                                  const a = filed && filed.appended.find((x) => x.key === t.key);
                                  const isPending = pending.includes(t.key);
                                  const rows = [(
                                    <tr key={t.key} className={isPending ? 'pending' : ''}>
                                      <td className="tk-td-n">{i + 1}</td>
                                      <td><span className={`tk-chip kind-${t.kind}`}>{t.kind}</span></td>
                                      <td className="tk-td-title">
                                        {c ? <button type="button" className="tk-linkbtn tk-title-link" onClick={() => onSelectTask(c.taskId)}>{t.title}</button>
                                          : a ? <button type="button" className="tk-linkbtn tk-title-link" onClick={() => onSelectTask(a.taskId)}>{t.title}</button>
                                          : <span>{t.title}</span>}
                                        {t.uncertainty ? <span className="tk-note-mark" title={t.uncertainty}>⚠</span> : null}
                                      </td>
                                      <td className="tk-td-where">{targetsLabel(t) || <span className="tk-muted">whole quest</span>}</td>
                                      <td className="tk-td-quest">{t.quest_id && t.quest_id !== questId ? questName(t.quest_id) : <span className="tk-muted">this quest</span>}</td>
                                      <td className="tk-td-state">
                                        {c ? <span className="tk-chip st-open">created</span> : a ? <span className="tk-chip st-done" title="The remark was added to an existing card">merged</span> : isPending ? <span className="tk-chip st-ready">waiting</span> : <span className="tk-chip st-dismissed">skipped</span>}
                                      </td>
                                      {perCard[im.id] ? (
                                        <td>
                                          {isPending ? (
                                            <select value={assign[t.key] || ''} onChange={(e) => setAssign((s) => ({ ...s, [t.key]: e.target.value }))} aria-label={`Quest for ${t.title}`}>
                                              <option value="">— keep waiting —</option>
                                              {(quests || []).map((q) => <option key={q.quest_id} value={q.quest_id}>{q.quest_name || q.quest_id} · {q.quest_id}</option>)}
                                              <option value="skip">Discard</option>
                                            </select>
                                          ) : null}
                                        </td>
                                      ) : null}
                                    </tr>
                                  )];
                                  if (showNotes[im.id] && t.uncertainty) {
                                    rows.push(<tr key={`${t.key}-n`} className="tk-tr-note"><td /><td /><td colSpan={perCard[im.id] ? 5 : 4} className="tk-td-note">⚠ {t.uncertainty}</td></tr>);
                                  }
                                  return rows;
                                })}
                              </tbody>
                            </table>
                          </>
                        ) : <div className="tk-muted">No remarks found in this text.</div>}
                        {others.length ? <div className="tk-muted tk-others">Read as not a remark: {others.map((m) => `#${m.idx + 1} ${m.kind}`).join(', ')}</div> : null}
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
